/**
 * Page du tableau de bord parent.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { inscriptionService, documentService, paiementService } from '../services/api';
import logger from '../utils/logger';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [taches, setTaches] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // OPTIMISÉ: Charger toutes les données en parallèle (3 requêtes au lieu de 1+N*2)
      const [inscriptionsResp, documentsResp, paiementsResp] = await Promise.all([
        inscriptionService.getAll(),
        documentService.getAll(),
        paiementService.getAll(),
      ]);

      const inscriptionsData = Array.isArray(inscriptionsResp.data)
        ? inscriptionsResp.data
        : (inscriptionsResp.data.results || []);

      const allDocuments = Array.isArray(documentsResp.data)
        ? documentsResp.data
        : (documentsResp.data.results || []);

      const allPaiements = Array.isArray(paiementsResp.data)
        ? paiementsResp.data
        : (paiementsResp.data.results || []);

      // OPTIMISÉ: Grouper les documents et paiements par inscription côté client
      const documentsByInscription = {};
      const paiementsByInscription = {};

      // Initialiser les dictionnaires
      inscriptionsData.forEach(inscription => {
        documentsByInscription[inscription.id] = [];
        paiementsByInscription[inscription.id] = [];
      });

      // Grouper les documents
      allDocuments.forEach(doc => {
        if (doc.inscription && documentsByInscription[doc.inscription]) {
          documentsByInscription[doc.inscription].push(doc);
        }
      });

      // Grouper les paiements
      allPaiements.forEach(paiement => {
        if (paiement.inscription && paiementsByInscription[paiement.inscription]) {
          paiementsByInscription[paiement.inscription].push(paiement);
        }
      });

      // Générer la liste des tâches
      genererTaches(inscriptionsData, documentsByInscription, paiementsByInscription);

      logger.log(`✅ OPTIMISÉ: Dashboard Parent chargé avec 3 requêtes au lieu de ${1 + inscriptionsData.length * 2}`);

    } catch (error) {
      logger.error('Erreur:', error);
    } finally {
      setLoading(false);
    }
  };

  const genererTaches = (inscriptions, documentsByInscription, paiementsByInscription) => {
    const tachesListe = [];

    for (const inscription of inscriptions) {
      // Ignorer les inscriptions annulées
      if (inscription.statut === 'ANNULEE') continue;

      const enfantNom = `${inscription.enfant_details?.prenom} ${inscription.enfant_details?.nom}`;
      const sejourTitre = inscription.sejour_details?.titre || 'Séjour';
      const inscriptionLabel = `${enfantNom} - ${sejourTitre}`;

      // Tâche 1: Compléter les informations si manquantes
      if (!inscription.is_infos_complete && inscription.statut !== 'EN_ATTENTE_VALIDATION_PARTENAIRE') {
        tachesListe.push({
          id: `infos-${inscription.id}`,
          type: 'urgent',
          titre: `Informations incomplètes`,
          description: inscriptionLabel,
          action: () => navigate(`/inscriptions/${inscription.id}?tab=infos`),
          actionLabel: 'Compléter',
          icon: '⚠️'
        });
      }

      // Tâche 2: Envoyer les documents manquants
      // OPTIMISÉ: Utiliser les documents préchargés au lieu d'un appel API
      const documents = documentsByInscription[inscription.id] || [];

      // Documents refusés à renvoyer
      const docsRefuses = documents.filter(d => d.statut_validation === 'REFUSE');
      if (docsRefuses.length > 0) {
        tachesListe.push({
          id: `docs-refuses-${inscription.id}`,
          type: 'urgent',
          titre: `${docsRefuses.length} document${docsRefuses.length > 1 ? 's' : ''} refusé${docsRefuses.length > 1 ? 's' : ''}`,
          description: inscriptionLabel,
          action: () => navigate(`/inscriptions/${inscription.id}?tab=documents`),
          actionLabel: 'Corriger',
          icon: '❌'
        });
      }

      // Documents en attente de validation - masqué pour les parents
      // const docsEnAttente = documents.filter(d => d.statut_validation === 'EN_ATTENTE');
      // if (docsEnAttente.length > 0) {
      //   tachesListe.push({
      //     id: `docs-attente-${inscription.id}`,
      //     type: 'info',
      //     titre: `${docsEnAttente.length} document(s) en attente de validation`,
      //     description: `${enfantNom} - ${sejourTitre}`,
      //     action: () => navigate(`/inscriptions/${inscription.id}`),
      //     actionLabel: 'Voir',
      //     icon: '⏳'
      //   });
      // }

      // Vérifier s'il manque des documents requis ou non validés
      const typesRequis = ['FICHE_SANITAIRE', 'VACCINS', 'ATTESTATION_AISANCE_AQUATIQUE'];
      // Ne compter comme présents que les documents VALIDES
      const typesValides = new Set(documents.filter(d => d.statut_validation === 'VALIDE').map(d => d.type_document));
      const typesManquantsOuNonValides = typesRequis.filter(type => !typesValides.has(type));

      if (typesManquantsOuNonValides.length > 0) {
        // Déterminer combien sont réellement manquants vs en attente
        const docsExistants = new Set(documents.map(d => d.type_document));
        const vraimentManquants = typesManquantsOuNonValides.filter(type => !docsExistants.has(type));

        tachesListe.push({
          id: `docs-manquants-${inscription.id}`,
          type: 'important',
          titre: `${typesManquantsOuNonValides.length} document${typesManquantsOuNonValides.length > 1 ? 's' : ''} en attente${vraimentManquants.length > 0 ? ` (${vraimentManquants.length} manquant${vraimentManquants.length > 1 ? 's' : ''})` : ''}`,
          description: inscriptionLabel,
          action: () => navigate(`/inscriptions/${inscription.id}?tab=documents`),
          actionLabel: vraimentManquants.length > 0 ? 'Envoyer' : 'Gérer',
          icon: '📄'
        });
      }

      // Tâche 3 & 4: Gérer les paiements
      // OPTIMISÉ: Utiliser les paiements préchargés au lieu d'un appel API
      const paiements = paiementsByInscription[inscription.id] || [];
      const paiementsEnAttente = paiements.filter(p => p.statut === 'EN_ATTENTE');
      const montantEnAttente = paiementsEnAttente.reduce((sum, p) => sum + parseFloat(p.montant), 0);

      // Calculer le vrai solde restant (en excluant les paiements en attente)
      const soldeReelRestant = inscription.solde_restant - montantEnAttente;

      // Afficher les paiements en attente de validation - masqué pour les parents
      // if (paiementsEnAttente.length > 0) {
      //   tachesListe.push({
      //     id: `paiements-attente-${inscription.id}`,
      //     type: 'info',
      //     titre: `${paiementsEnAttente.length} paiement(s) en attente de validation (${montantEnAttente.toFixed(2)}€)`,
      //     description: `${enfantNom} - ${sejourTitre}`,
      //     action: () => navigate(`/inscriptions/${inscription.id}`),
      //     actionLabel: 'Voir',
      //     icon: '⏳'
      //   });
      // }

      // Afficher la demande de paiement SEULEMENT s'il reste vraiment un solde après les paiements en attente
      if (soldeReelRestant > 0.01) { // Utiliser 0.01 pour éviter les problèmes d'arrondi
        tachesListe.push({
          id: `paiement-${inscription.id}`,
          type: 'important',
          titre: `Reste à payer : ${soldeReelRestant.toFixed(2)}€`,
          description: inscriptionLabel,
          action: () => navigate(`/inscriptions/${inscription.id}?tab=paiements`),
          actionLabel: 'Payer',
          icon: '💳'
        });
      }
    }

    setTaches(tachesListe);
  };

  if (loading) {
    return <div className="text-center py-12">Chargement...</div>;
  }

  return (
    <div className="w-full min-h-screen">
      <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">
            Bienvenue, {user?.first_name} {user?.last_name}
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-2">Voici un récapitulatif de vos actions en cours</p>
        </div>

        {/* Raccourcis rapides */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-8">
          <button
            onClick={() => navigate('/enfants')}
            className="bg-white border-2 border-blue-500 p-4 sm:p-5 rounded-lg hover:bg-blue-50 hover:shadow-md transition-all text-left group cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center text-xl sm:text-2xl flex-shrink-0">
                  👶
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 text-sm sm:text-base">Ajouter un enfant</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Créer une fiche enfant</p>
                </div>
              </div>
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-blue-500 group-hover:translate-x-1 transition-transform flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>

          <button
            onClick={() => navigate('/sejours')}
            className="bg-white border-2 border-green-500 p-4 sm:p-5 rounded-lg hover:bg-green-50 hover:shadow-md transition-all text-left group cursor-pointer"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center text-xl sm:text-2xl flex-shrink-0">
                  🏖️
                </div>
                <div className="min-w-0">
                  <h3 className="font-bold text-gray-900 text-sm sm:text-base">Nouvelle inscription</h3>
                  <p className="text-xs sm:text-sm text-gray-600">Inscrire à un séjour</p>
                </div>
              </div>
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-500 group-hover:translate-x-1 transition-transform flex-shrink-0 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        </div>

        {/* Liste des tâches à faire */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Actions requises</h2>

          {taches.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <div className="text-4xl sm:text-6xl mb-4">✓</div>
              <p className="text-green-600 text-lg sm:text-xl font-semibold">Tout est à jour !</p>
              <p className="text-sm sm:text-base text-gray-500 mt-2">Vous n'avez aucune action en attente</p>
            </div>
          ) : (
            <div className="space-y-3">
              {taches.map((tache) => (
                <div
                  key={tache.id}
                  className={`border-l-4 p-3 sm:p-4 rounded-r-lg flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${
                    tache.type === 'urgent' ? 'border-red-500 bg-red-50' :
                    tache.type === 'important' ? 'border-orange-500 bg-orange-50' :
                    'border-blue-500 bg-blue-50'
                  }`}
                >
                  <div className="flex items-start space-x-2 sm:space-x-3 flex-1 min-w-0">
                    <span className="text-xl sm:text-2xl flex-shrink-0">{tache.icon}</span>
                    <div className="min-w-0 flex-1">
                      <h3 className="font-semibold text-gray-900 text-sm sm:text-base break-words">{tache.titre}</h3>
                      <p className="text-xs sm:text-sm text-gray-600 break-words">{tache.description}</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      tache.action();
                    }}
                    className={`px-4 py-2 rounded font-medium text-white text-sm whitespace-nowrap self-start sm:self-center ${
                      tache.type === 'urgent' ? 'bg-red-600 hover:bg-red-700 active:bg-red-800' :
                      tache.type === 'important' ? 'bg-orange-600 hover:bg-orange-700 active:bg-orange-800' :
                      'bg-blue-600 hover:bg-blue-700 active:bg-blue-800'
                    }`}
                  >
                    {tache.actionLabel}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
